import { isDateOnlyBefore } from "@common/helpers/date";
import { capitalize } from "@common/helpers/string";
import {
    isTaskTerminatingStatus,
    TaskStatus,
    type TaskType,
} from "@common/types/task";
import { TASK_TYPE_LABEL } from "@components/settings/OrganizationCloseTemplates/closeTemplateTasksDrawer.constants";
import type {
    BoardLock,
    BoardMoveCheck,
    BoardMoveVerdict,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import {
    DEMO_ENTITIES,
    DEMO_PEOPLE,
    type DemoEntity,
    type DemoStageId,
    type DemoTask,
} from "./fixtures/demoTasks";

export const NO_STAGE_COLUMN_ID = "no-stage";

export interface TaskStageColumn {
    id: DemoStageId | typeof NO_STAGE_COLUMN_ID;
    title: string;
}

export const TASK_STAGE_COLUMNS: readonly TaskStageColumn[] = [
    { id: NO_STAGE_COLUMN_ID, title: "No stage" },
    { id: "intake", title: "Intake" },
    { id: "prep", title: "Prep" },
    { id: "adjustments", title: "Adjustments" },
    { id: "review", title: "Review" },
    { id: "deliver", title: "Deliver" },
];

export const getTaskId = (task: DemoTask) => task.id;

export const getTaskLabel = (task: DemoTask) => task.title;

const CANCELED_LOCK: BoardLock = { reason: "Canceled tasks can't be moved" };

export const getTaskLock = (task: DemoTask) =>
    task.status === TaskStatus.CANCELLED ? CANCELED_LOCK : null;

const DELIVER_RULE = "Only done tasks can move to Deliver";

/** Playground rule for stage columns: only done tasks can move to Deliver. */
export const canMoveTask = ({
    item,
    to,
}: BoardMoveCheck<DemoTask>): BoardMoveVerdict =>
    to.columnId === "deliver" && item.status !== TaskStatus.DONE
        ? { isAllowed: false, reason: DELIVER_RULE }
        : { isAllowed: true };

/** The same rule on a task as it stands: in the Deliver column but not done breaks it. */
export const getDeliverRuleBreak = (
    task: DemoTask,
    getPlacements: (task: DemoTask) => readonly BoardPlacement[],
) =>
    task.status !== TaskStatus.DONE &&
    getPlacements(task).some(({ columnId }) => columnId === "deliver")
        ? DELIVER_RULE
        : null;

export const getStageId = (columnId: string) => {
    const stage = TASK_STAGE_COLUMNS.find((column) => column.id === columnId);
    return stage === undefined || stage.id === NO_STAGE_COLUMN_ID
        ? null
        : stage.id;
};

export const isTaskOverdue = (task: DemoTask, todayDateOnly: string) =>
    !isTaskTerminatingStatus(task.status) &&
    task.dueDateOnly !== null &&
    isDateOnlyBefore(task.dueDateOnly, todayDateOnly);

interface TaskFilters {
    search: string | undefined;
    statuses: readonly TaskStatus[];
}

/** Checks a task against the title search and status filter. */
export const createTaskFilter = ({ search, statuses }: TaskFilters) => {
    const query = search?.trim().toLowerCase() ?? "";
    const selectedStatuses = new Set(statuses);
    return (task: DemoTask) =>
        (selectedStatuses.size === 0 || selectedStatuses.has(task.status)) &&
        (query === "" || task.title.toLowerCase().includes(query));
};

const PEOPLE_BY_ID = new Map(DEMO_PEOPLE.map((person) => [person.id, person]));
const ENTITIES_BY_ID = new Map(
    DEMO_ENTITIES.map((entity) => [entity.id, entity]),
);

export const getPerson = (personId: string | null | undefined) =>
    personId ? PEOPLE_BY_ID.get(personId) : undefined;

export const getAssignee = (task: DemoTask | undefined) =>
    getPerson(task?.assigneeId);

const NO_ENTITIES: readonly DemoEntity[] = [];
// Tasks are replaced, never mutated, so each one's entities are found once
// and the memoized card gets the same array on every render.
const entitiesByTask = new WeakMap<DemoTask, readonly DemoEntity[]>();

export const getEntities = (task: DemoTask | undefined) => {
    if (task === undefined) {
        return NO_ENTITIES;
    }
    const cached = entitiesByTask.get(task);
    if (cached !== undefined) {
        return cached;
    }
    const entities = task.entityIds.flatMap((entityId) => {
        const entity = ENTITIES_BY_ID.get(entityId);
        return entity === undefined ? [] : [entity];
    });
    entitiesByTask.set(task, entities);
    return entities;
};

/** The product's task type label; types it doesn't name are capitalised ("File"). */
export const getTaskTypeLabel = (type: TaskType) =>
    TASK_TYPE_LABEL[type] ?? capitalize(type.replaceAll("_", " "));

export const getStageTitle = (task: DemoTask | undefined) =>
    TASK_STAGE_COLUMNS.find(
        (stage) => stage.id === (task?.stageId ?? NO_STAGE_COLUMN_ID),
    )?.title ?? "";
